/**
 * Action Types
 *
 * Type definitions for server action results and parameters
 */

// ============================================================================
// ACTION RESULT TYPES
// ============================================================================

/**
 * Standard result type for server actions
 */
export interface ActionResult<T = unknown> {
  success: boolean;
  data?: T;
  statusCode?: number;
  error?: string;
  validationErrors?: Array<{
    field: string;
    message: string;
  }>;
}

/**
 * Result type for paginated actions
 */
export interface PaginatedActionResult<T> extends ActionResult<T[]> {
  pagination?: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

/**
 * Result type for batch operations
 */
export interface BatchActionResult<T = unknown> {
  success: boolean;
  successCount: number;
  failureCount: number;
  results: Array<{
    id: string | number;
    success: boolean;
    data?: T;
    error?: string;
  }>;
}

// ============================================================================
// ACTION PARAMETER TYPES
// ============================================================================

/**
 * Common filter parameters for list actions
 */
export interface ListFilters {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  search?: string;
  status?: string | string[];
  dateFrom?: string;
  dateTo?: string;
}

/**
 * Parameters for matter-related actions
 */
export interface MatterActionParams {
  matterId: string | number;
}

/**
 * Parameters for file upload actions
 */
export interface FileUploadParams {
  matterId: string | number;
  file: File;
  fileType: string;
  description?: string;
  isCertifiedCopy?: boolean;
}

/**
 * Parameters for task actions
 */
export interface TaskActionParams extends MatterActionParams {
  title: string;
  description?: string;
  dueDate?: string;
  priority?: 'Low' | 'Medium' | 'High' | 'Urgent';
  status?: 'Todo' | 'InProgress' | 'Completed' | 'Cancelled';
  assigneeId?: number;
}

/**
 * Parameters for contact actions
 */
export interface ContactActionParams extends MatterActionParams {
  fullName: string;
  email?: string;
  phone?: string;
  role?: string;
  organization?: string;
  type?: 'primary' | 'beneficiary' | 'professional' | 'other';
  isPrimary?: boolean;
}

/**
 * Parameters for inventory actions
 */
export interface InventoryActionParams extends MatterActionParams {
  description: string;
  category: string;
  value?: number;
  quantity?: number;
  location?: string;
  notes?: string;
}

/**
 * Parameters for communication actions
 */
export interface CommunicationActionParams extends MatterActionParams {
  type: string;
  subject?: string;
  content?: string;
  direction: 'INCOMING' | 'OUTGOING';
  contactId?: number;
  scheduledFor?: string;
}

// ============================================================================
// TYPE GUARDS
// ============================================================================

/**
 * Type guard to check if result is successful
 */
export function isSuccessResult<T>(
  result: ActionResult<T>
): result is ActionResult<T> & { data: T } {
  return result.success && result.data !== undefined;
}

/**
 * Type guard to check if result has validation errors
 */
export function hasValidationErrors(result: ActionResult<unknown>): boolean {
  return (
    !result.success &&
    Array.isArray(result.validationErrors) &&
    result.validationErrors.length > 0
  );
}

/**
 * Type guard to check if error is an API error
 */
export function isApiError(
  error: unknown
): error is { message: string; code?: string } {
  return (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof (error as { message: unknown }).message === 'string'
  );
}
